import React, { useContext, useEffect } from "react";
import { AuthConfig, AuthContext } from "./AuthContext";
import { Login } from "./login";
import { useToken } from "./useToken";

// Re-export for backward compatibility
export { AuthConfig, AuthContext, type AzureConfig } from "./AuthContext";

const getBase = () =>
	document.getElementsByTagName("base")[0]?.getAttribute("href") || "/";

type AuthProviderProps = {
	children: any;
	authConfig: AuthConfig;
};

export const AuthProvider = ({ children, authConfig }: AuthProviderProps) => {
	const useTokenHook = useToken();
	return (
		<>
			<AuthContext.Provider value={useTokenHook}>
				{!!useTokenHook.token && <Authenticated>{children}</Authenticated>}
				{!useTokenHook.token && <Unauthenticated authConfig={authConfig} />}
			</AuthContext.Provider>
		</>
	);
};

const Authenticated = ({ children }: { children: any }) => {
	const redirectTo = new URL(document.location as any).searchParams.get(
		"redirectTo",
	);

	useEffect(() => {
		if (redirectTo) {
			window.location.href = decodeURIComponent(redirectTo);
		}
	}, []);

	if (redirectTo) {
		return null;
	}

	return <>{children}</>;
};

const Unauthenticated = ({ authConfig }: { authConfig: AuthConfig }) => {
	const { clearUserData } = useContext(AuthContext);
	const currentUrl = new URL(document.location as any);
	const base = getBase();
	const basePath = base.endsWith("/") ? base.slice(0, -1) : base;
	const isRootPath =
		currentUrl.pathname === "/" ||
		currentUrl.pathname === base ||
		currentUrl.pathname === basePath;
	const hasRedirectTo = currentUrl.searchParams.has("redirectTo");
	const shouldShowError = !isRootPath && !hasRedirectTo;

	useEffect(() => {
		clearUserData();

		if (shouldShowError) {
			const fullPath = currentUrl.pathname + currentUrl.search;
			const newParams = new URLSearchParams();
			newParams.set("redirectTo", fullPath);
			newParams.set("error", "Session expired. Please log in to continue");

			const newUrl = `${window.location.origin}${base}?${newParams.toString()}`;
			window.history.replaceState({ path: newUrl }, "", newUrl);
		}
	}, []);

	return (
		<Login
			authConfig={authConfig}
			initialError={
				shouldShowError
					? "Session expired. Please log in to continue"
					: undefined
			}
		/>
	);
};
